import { Edge } from "@xyflow/react";
import { TaskRegistry } from "src/constants/workflow.constants";
import {
  WorkflowExecutionPlan,
  AppNodeMissingInputs,
  AppNode,
  WorkflowExecutionPlanPhase,
} from "src/types/workflow.types";

export enum FlowToExecutionPlanValidationError {
  "NO_ENTRY_POINT",
  "INVALID_INPUTS",
}

type FlowToExecutionPlanType = {
  executionPlan?: WorkflowExecutionPlan;
  error?: {
    type: FlowToExecutionPlanValidationError;
    invalidElements?: AppNodeMissingInputs[];
  };
};

export function FlowToExecutionPlan(
  nodes: AppNode[],
  edges: Edge[],
): FlowToExecutionPlanType {
  // Input validation
  if (!nodes?.length) {
    throw new Error("No nodes provided");
  }

  const entryPoint = nodes.find(
    (node) => TaskRegistry[node.data.type].isEntryPoint,
  );

  if (!entryPoint) {
    return {
      error: { type: FlowToExecutionPlanValidationError.NO_ENTRY_POINT },
    };
  }

  const planned = new Set<string>([entryPoint.id]);
  const inputsWithErrors: AppNodeMissingInputs[] = [];
  const executionPlan: WorkflowExecutionPlanPhase[] = [];

  const recordErrors = (list: AppNode[]): void => {
    list.forEach((node) => {
      const invalid = getInvalidInputs(node, edges, planned);
      if (invalid.length) {
        inputsWithErrors.push({ nodeId: node.id, inputs: invalid });
        console.error(
          `Node ${node.id} has invalid inputs: ${invalid.join(", ")}`,
        );
      }
    });
  };

  const schedulePhase = (phaseNum: number, list: AppNode[]) => {
    recordErrors(list);
    executionPlan.push({ phase: phaseNum, nodes: list });
    list.forEach((n) => planned.add(n.id));
  };

  schedulePhase(1, [entryPoint]);

  let phase = 2;
  while (planned.size < nodes.length) {
    const candidates = nodes.filter((n) => !planned.has(n.id));

    const ready = candidates.filter((node) => {
      const invalid = getInvalidInputs(node, edges, planned);
      const allIncomers = getIncomers(node, nodes, edges);
      const depsReady = allIncomers.every((inc) => planned.has(inc.id));
      return invalid.length === 0 || depsReady;
    });

    if (!ready.length) break;
    schedulePhase(phase++, ready);
  }

  if (inputsWithErrors.length) {
    return {
      error: {
        type: FlowToExecutionPlanValidationError.INVALID_INPUTS,
        invalidElements: inputsWithErrors,
      },
    };
  }

  return { executionPlan };
}

/**
 * Determines which inputs of a given node are invalid based on the current edges and planned nodes.
 *
 * @param node - The node whose inputs are to be validated.
 * @param edges - An array of edges representing connections between nodes.
 * @param planned - A set of node IDs that are planned or already processed.
 * @returns An array of input names that are considered invalid.
 */
function getInvalidInputs(
  node: AppNode,
  edges: Edge[],
  planned: Set<string>,
): string[] {
  const invalidInputs: string[] = [];
  const inputs = TaskRegistry[node.data.type].inputs;
  const incomingEdgeByHandle = new Map<string, Edge>();

  for (const edge of edges) {
    if (edge.target === node.id) {
      incomingEdgeByHandle.set(edge.targetHandle, edge);
    }
  }

  for (const input of inputs) {
    const value = node.data.inputs[input.name];
    const hasValue = Boolean(value && value.length > 0);
    const incoming = incomingEdgeByHandle.get(input.name);

    const missingRequired =
      input.required &&
      !hasValue &&
      (!incoming || !planned.has(incoming.source));

    const invalidOptional =
      !input.required &&
      hasValue &&
      incoming !== undefined &&
      !planned.has(incoming.source);

    if (missingRequired || invalidOptional) {
      invalidInputs.push(input.name);
    }
  }

  return invalidInputs;
}

function getIncomers(
  node: AppNode,
  nodes: AppNode[],
  edges: Edge[],
): AppNode[] {
  if (!node.id) {
    return [];
  }
  const incomersIds = new Set<string>();

  edges.forEach((edge) => {
    if (edge.target === node.id) {
      incomersIds.add(edge.source);
    }
  });

  return nodes.filter((n) => incomersIds.has(n.id));
}
